import React, { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { DialogTextActionButton } from "../../Base/DialogTextActionButton";
import { RegistrationPhase, Tournoi } from "../../../types";

const REGISTRATION_PHASE_OPTIONS: { value: RegistrationPhase; label: string }[] = [
  { value: "closed", label: "Fermées (tournoi annoncé, inscriptions pas encore ouvertes)" },
  { value: "preRegistration", label: "Pré-inscription (clubs organisateurs + admins)" },
  { value: "open", label: "Ouvertes (tout le monde)" },
  { value: "closedForPlayers", label: "Fermées aux joueurs (admins uniquement)" },
  { value: "cancelled", label: "Annulée (tournoi annulé, inscriptions closes)" },
];

const EMPTY_FORM: Omit<Tournoi, "id"> = {
  label: "",
  key: "",
  current: false,
  isRegisterClosed: false,
  isRegisterClosedAdmin: false,
  registrationPhase: "closed",
  organizerClubIds: [],
  womenOnly: false,
  mail: "",
  adresse: "",
  lieuCourt: "",
  maps: "",
  mapsTitle: "",
  reglement: "",
  liveRefreshPeriodInSeconds: 10,
  statsCollectionName: "",
  dateOuvertureInscriptions: "",
  dateFinInscriptions: "",
  supplementApresDate: "",
  registrationPhaseClosed: false,
  alertThresholds: {
    cree: { "2": 60, "3": 20 },
    enCours: { "2": 50, "3": 100 },
  },
  estimatedMatchDurationMinutes: {
    "2": 40,
    "3": 90,
  },
};

type AlertState = "cree" | "enCours";
type AlertNb = "2" | "3";

interface TournamentFormDialogProps {
  open: boolean;
  mode: "create" | "edit";
  initialData?: Tournoi;
  allTournois: Tournoi[];
  onClose: () => void;
  onSubmit: (data: Omit<Tournoi, "id">) => Promise<void>;
}

const TournamentFormDialog: React.FC<TournamentFormDialogProps> = ({
  open,
  mode,
  initialData,
  allTournois,
  onClose,
  onSubmit,
}) => {
  const [form, setForm] = useState<Omit<Tournoi, "id">>(EMPTY_FORM);
  const [copySourceId, setCopySourceId] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);
  // organizerClubIds édité comme texte séparé par des virgules
  const [organizerClubIdsText, setOrganizerClubIdsText] = useState<string>("");

  const keyConflict = allTournois.find(
    (t) => t.key === form.key && t.id !== initialData?.id
  );
  const keyError = form.key && keyConflict
    ? `Clé déjà utilisée par "${keyConflict.label}"`
    : undefined;

  useEffect(() => {
    if (open) {
      if (mode === "edit" && initialData) {
        const { id: _id, tableaux: _tableaux, ...rest } = initialData as Tournoi & { tableaux?: unknown };
        setForm({ ...EMPTY_FORM, ...rest });
        setOrganizerClubIdsText((rest.organizerClubIds ?? []).join(", "));
      } else {
        setForm(EMPTY_FORM);
        setOrganizerClubIdsText("");
        setCopySourceId("");
      }
    }
  }, [open, mode, initialData]);

  const handleCopySource = (sourceId: string) => {
    setCopySourceId(sourceId);
    if (!sourceId) return;
    const source = allTournois.find((t) => t.id === sourceId);
    if (!source) return;
    setForm((prev) => ({
      ...prev,
      mail: source.mail,
      adresse: source.adresse,
      lieuCourt: source.lieuCourt ?? "",
      maps: source.maps,
      mapsTitle: source.mapsTitle,
      reglement: source.reglement,
      liveRefreshPeriodInSeconds: source.liveRefreshPeriodInSeconds,
      statsCollectionName: source.statsCollectionName ?? "",
      alertThresholds: source.alertThresholds ?? EMPTY_FORM.alertThresholds,
      estimatedMatchDurationMinutes:
        source.estimatedMatchDurationMinutes ??
        EMPTY_FORM.estimatedMatchDurationMinutes,
      home: source.home,
      frontBaseUrl: source.frontBaseUrl,
      hostingSiteId: source.hostingSiteId,
      pricingTag: source.pricingTag,
    }));
    setOrganizerClubIdsText((source.organizerClubIds ?? []).join(", "));
  };

  const setField = <K extends keyof Omit<Tournoi, "id">>(
    key: K,
    value: Omit<Tournoi, "id">[K]
  ) => setForm((prev) => ({ ...prev, [key]: value }));

  const setAlertThreshold = (
    state: AlertState,
    nb: AlertNb,
    value: string
  ) => {
    const parsed = parseInt(value, 10);
    setForm((prev) => ({
      ...prev,
      alertThresholds: {
        ...prev.alertThresholds!,
        [state]: {
          ...prev.alertThresholds![state],
          [nb]: isNaN(parsed) ? 0 : parsed,
        },
      },
    }));
  };

  const setEstimatedDuration = (nb: AlertNb, value: string) => {
    const parsed = parseInt(value, 10);
    setForm((prev) => ({
      ...prev,
      estimatedMatchDurationMinutes: {
        ...(prev.estimatedMatchDurationMinutes ??
          EMPTY_FORM.estimatedMatchDurationMinutes!),
        [nb]: isNaN(parsed) ? 0 : parsed,
      },
    }));
  };

  const handleOrganizerClubIdsChange = (text: string) => {
    setOrganizerClubIdsText(text);
    const ids = text
      .split(",")
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    setField("organizerClubIds", ids);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await onSubmit(form);
    } finally {
      setSubmitting(false);
    }
  };

  const alertThresholds = form.alertThresholds ?? EMPTY_FORM.alertThresholds!;
  const estimatedDurations =
    form.estimatedMatchDurationMinutes ??
    EMPTY_FORM.estimatedMatchDurationMinutes!;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {mode === "create" ? "Créer un tournoi" : `Modifier "${initialData?.label}"`}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={3}>
          {mode === "create" && (
            <>
              <TextField
                select
                label="Copier les infos d'un tournoi existant (optionnel)"
                value={copySourceId}
                onChange={(e) => handleCopySource(e.target.value)}
                fullWidth
                size="small"
              >
                <MenuItem value="">— Aucun —</MenuItem>
                {allTournois.map((t) => (
                  <MenuItem key={t.id} value={t.id}>
                    {t.label} ({t.key})
                  </MenuItem>
                ))}
              </TextField>
              <Divider />
            </>
          )}

          <Typography variant="subtitle2" color="text.secondary">
            Identité
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={8}>
              <TextField
                label="Nom du tournoi"
                value={form.label}
                onChange={(e) => setField("label", e.target.value)}
                fullWidth
                required
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Clé (identifiant unique)"
                value={form.key}
                onChange={(e) => setField("key", e.target.value)}
                fullWidth
                required
                size="small"
                error={!!keyError}
                helperText={keyError ?? "Ex: tournoi-2026 — utilisée dans l'URL ?tournoi="}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="E-mail de contact"
                value={form.mail}
                onChange={(e) => setField("mail", e.target.value)}
                fullWidth
                size="small"
                type="email"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Lieu court (affichage Home)"
                value={form.lieuCourt ?? ""}
                onChange={(e) => setField("lieuCourt", e.target.value)}
                fullWidth
                size="small"
                helperText="Ex: Gymnase X, Ville"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Adresse complète"
                value={form.adresse}
                onChange={(e) => setField("adresse", e.target.value)}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField
                label="Lien Google Maps (iframe)"
                value={form.maps}
                onChange={(e) => setField("maps", e.target.value)}
                fullWidth
                size="small"
                helperText='URL seule commençant par https://www.google.com/maps/embed?pb=… (depuis Partager → Intégrer une carte → copier uniquement le src). Pas de &#39; dans l’URL : utiliser une apostrophe normale ou %27.'
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Titre du lien Maps"
                value={form.mapsTitle}
                onChange={(e) => setField("mapsTitle", e.target.value)}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Règlement (URL ou texte)"
                value={form.reglement}
                onChange={(e) => setField("reglement", e.target.value)}
                fullWidth
                size="small"
                helperText="Nom du fichier dans public/ (ex. reglement-tournoi-national-2026.pdf). Éviter accents dans l’URL : certains domaines / CDN renvoient vers la SPA sinon."
              />
            </Grid>
          </Grid>

          <Divider />
          <Typography variant="subtitle2" color="text.secondary">
            Contenu Home (marketing)
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Ligne week-end compétition"
                value={form.home?.weekendLine ?? ""}
                onChange={(e) =>
                  setField("home", {
                    ...form.home,
                    weekendLine: e.target.value,
                  })
                }
                fullWidth
                size="small"
                helperText="Ex: 31 octobre et 1er novembre 2026 — vide = fallback site"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Ligne dotations"
                value={form.home?.dotationsLine ?? ""}
                onChange={(e) =>
                  setField("home", {
                    ...form.home,
                    dotationsLine: e.target.value,
                  })
                }
                fullWidth
                size="small"
                helperText="Ex: 2 610 € de dotations"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="URL affiche (poster)"
                value={form.home?.posterUrl ?? ""}
                onChange={(e) =>
                  setField("home", {
                    ...form.home,
                    posterUrl: e.target.value,
                  })
                }
                fullWidth
                size="small"
                helperText="Ex: /2026.png ou URL Storage"
                disabled={form.home?.hidePoster === true}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.home?.hidePoster === true}
                    onChange={(e) =>
                      setField("home", {
                        ...form.home,
                        hidePoster: e.target.checked,
                      })
                    }
                  />
                }
                label="Masquer l'affiche (pas de fallback)"
              />
              <FormHelperText sx={{ ml: 0 }}>
                Si coché : aucune affiche sur la home, même sans URL. N’affecte
                que ce tournoi.
              </FormHelperText>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Heure d'ouverture inscriptions"
                value={form.home?.registrationOpenTimeLabel ?? ""}
                onChange={(e) =>
                  setField("home", {
                    ...form.home,
                    registrationOpenTimeLabel: e.target.value,
                  })
                }
                fullWidth
                size="small"
                helperText="Ex: 12 h"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Pourquoi participer — organisation"
                value={form.home?.whyParticipateOrganization ?? ""}
                onChange={(e) =>
                  setField("home", {
                    ...form.home,
                    whyParticipateOrganization: e.target.value,
                  })
                }
                fullWidth
                size="small"
                multiline
                minRows={2}
                helperText="Vide = fallback site (ex. 32 tables…). JN : 16 tables…"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Pourquoi participer — niveaux / récompenses"
                value={form.home?.whyParticipateLevels ?? ""}
                onChange={(e) =>
                  setField("home", {
                    ...form.home,
                    whyParticipateLevels: e.target.value,
                  })
                }
                fullWidth
                size="small"
                multiline
                minRows={2}
                helperText="Vide = fallback site (ex. jusqu'à 350 €…)"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Phrase tables (home close / live)"
                value={form.home?.spectatorTablesLine ?? ""}
                onChange={(e) =>
                  setField("home", {
                    ...form.home,
                    spectatorTablesLine: e.target.value,
                  })
                }
                fullWidth
                size="small"
                helperText="Ex: 16 tables et buvette sur place…"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Alt affiche"
                value={form.home?.posterAlt ?? ""}
                onChange={(e) =>
                  setField("home", {
                    ...form.home,
                    posterAlt: e.target.value,
                  })
                }
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Galerie (URLs, une par ligne)"
                value={(form.home?.galleryUrls ?? []).join("\n")}
                onChange={(e) =>
                  setField("home", {
                    ...form.home,
                    galleryUrls: e.target.value
                      .split("\n")
                      .map((s) => s.trim())
                      .filter((s) => s.length > 0),
                  })
                }
                fullWidth
                size="small"
                multiline
                minRows={3}
                helperText="Chemins public/ ou URLs. Vide = galerie par défaut."
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Highlights — eyebrow"
                value={form.home?.highlightsEyebrow ?? ""}
                onChange={(e) =>
                  setField("home", {
                    ...form.home,
                    highlightsEyebrow: e.target.value,
                  })
                }
                fullWidth
                size="small"
                helperText="Ex: Temps forts"
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField
                label="Highlights — titre"
                value={form.home?.highlightsTitle ?? ""}
                onChange={(e) =>
                  setField("home", {
                    ...form.home,
                    highlightsTitle: e.target.value,
                  })
                }
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Highlights — sous-titre"
                value={form.home?.highlightsSubtitle ?? ""}
                onChange={(e) =>
                  setField("home", {
                    ...form.home,
                    highlightsSubtitle: e.target.value,
                  })
                }
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Highlights (une ligne : titre | texte | icône)"
                value={(form.home?.highlights ?? [])
                  .map((h) =>
                    [h.title, h.body, h.icon ?? ""].filter(Boolean).join(" | ")
                  )
                  .join("\n")}
                onChange={(e) =>
                  setField("home", {
                    ...form.home,
                    highlights: e.target.value
                      .split("\n")
                      .map((line) => line.trim())
                      .filter((line) => line.length > 0)
                      .map((line) => {
                        const parts = line.split("|").map((p) => p.trim());
                        const title = parts[0] ?? "";
                        const body = parts[1] ?? "";
                        const icon = parts[2];
                        return icon
                          ? { title, body, icon }
                          : { title, body };
                      })
                      .filter((h) => h.title.length > 0 && h.body.length > 0),
                  })
                }
                fullWidth
                size="small"
                multiline
                minRows={4}
                helperText="Vide = section masquée (autres tournois). Icônes : marraine, ateliers, goodies, tarifs, convivialite, demiJournee."
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="URL publique du site (emails)"
                value={form.frontBaseUrl ?? ""}
                onChange={(e) => setField("frontBaseUrl", e.target.value)}
                fullWidth
                size="small"
                helperText="Ex: https://tournoi.sqyping.fr"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Site Hosting Firebase"
                value={form.hostingSiteId ?? ""}
                onChange={(e) => setField("hostingSiteId", e.target.value)}
                fullWidth
                size="small"
                helperText="Ex: sqyping-bb88b ou sqyping-tournoi-b"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Tag tarifs Stripe (pricingTag)"
                value={form.pricingTag ?? ""}
                onChange={(e) => setField("pricingTag", e.target.value)}
                fullWidth
                size="small"
                helperText="Vide = catalogue partagé. Sinon filtre products.metadata.pricingTag"
              />
            </Grid>
          </Grid>

          <Divider />
          <Typography variant="subtitle2" color="text.secondary">
            Phase des inscriptions
          </Typography>
          <Grid container spacing={2} alignItems="flex-start">
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Phase des inscriptions</InputLabel>
                <Select
                  label="Phase des inscriptions"
                  value={form.registrationPhase}
                  onChange={(e) =>
                    setField("registrationPhase", e.target.value as RegistrationPhase)
                  }
                >
                  {REGISTRATION_PHASE_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Date d'ouverture des inscriptions"
                value={form.dateOuvertureInscriptions ?? ""}
                onChange={(e) => setField("dateOuvertureInscriptions", e.target.value)}
                fullWidth
                size="small"
                helperText="Affichage informatif. Ex: 15 mai 2026"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Date de fin des inscriptions"
                value={form.dateFinInscriptions ?? ""}
                onChange={(e) => setField("dateFinInscriptions", e.target.value)}
                fullWidth
                size="small"
                helperText="Ex: 27 juin 2026"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Supplément après la date limite"
                value={form.supplementApresDate ?? ""}
                onChange={(e) => setField("supplementApresDate", e.target.value)}
                fullWidth
                size="small"
                helperText="Ex: 2€ par tableau"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Clubs organisateurs (pré-inscription)"
                value={organizerClubIdsText}
                onChange={(e) => handleOrganizerClubIdsChange(e.target.value)}
                fullWidth
                size="small"
                helperText="Identifiants FFTT des clubs autorisés en pré-inscription, séparés par des virgules. Ex: 08950017, 07840023"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(form.womenOnly)}
                    onChange={(e) => setField("womenOnly", e.target.checked)}
                    color="secondary"
                  />
                }
                label="Tournoi réservé aux femmes"
              />
              <FormHelperText sx={{ mt: 0, ml: 0 }}>
                Bloque l’inscription dès la saisie d’une licence masculine (FFTT).
              </FormHelperText>
            </Grid>
          </Grid>

          <Divider />
          <Typography variant="subtitle2" color="text.secondary">
            Options
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6}>
              <TextField
                label="Période de rafraîchissement live"
                value={form.liveRefreshPeriodInSeconds}
                onChange={(e) =>
                  setField(
                    "liveRefreshPeriodInSeconds",
                    parseInt(e.target.value, 10) || 10
                  )
                }
                fullWidth
                size="small"
                type="number"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">secondes</InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Collection de statistiques"
                value={form.statsCollectionName ?? ""}
                onChange={(e) => setField("statsCollectionName", e.target.value)}
                fullWidth
                size="small"
                helperText="Nom de la collection Firestore"
              />
            </Grid>
          </Grid>

          <Divider />
          <Typography variant="subtitle2" color="text.secondary">
            Seuils d&apos;alerte matchs (en minutes)
          </Typography>
          <Grid container spacing={2}>
            {(["cree", "enCours"] as AlertState[]).map((state) => (
              <React.Fragment key={state}>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    {state === "cree" ? "Matchs créés" : "Matchs en cours"}
                  </Typography>
                </Grid>
                {(["2", "3"] as AlertNb[]).map((nb) => (
                  <Grid item xs={6} sm={3} key={`${state}-${nb}`}>
                    <TextField
                      label={`${nb} joueurs`}
                      value={alertThresholds[state]?.[nb] ?? 0}
                      onChange={(e) =>
                        setAlertThreshold(state, nb, e.target.value)
                      }
                      fullWidth
                      size="small"
                      type="number"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">min</InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                ))}
              </React.Fragment>
            ))}
          </Grid>

          <Divider />
          <Typography variant="subtitle2" color="text.secondary">
            Durée estimée des matchs — libération des tables (minutes)
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Utilisée par Occupation → Maintenant (P75 observé juin 2025 :
            40 / 90). Distinct des seuils d&apos;alerte ci-dessus.
          </Typography>
          <Grid container spacing={2}>
            {(["2", "3"] as AlertNb[]).map((nb) => (
              <Grid item xs={6} sm={3} key={`est-${nb}`}>
                <TextField
                  label={`${nb} joueurs`}
                  value={estimatedDurations[nb] ?? 0}
                  onChange={(e) => setEstimatedDuration(nb, e.target.value)}
                  fullWidth
                  size="small"
                  type="number"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">min</InputAdornment>
                    ),
                  }}
                />
              </Grid>
            ))}
          </Grid>
        </Stack>
      </DialogContent>
      <DialogActions>
        <DialogTextActionButton onClick={onClose} disabled={submitting}>
          Annuler
        </DialogTextActionButton>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={submitting || !form.label || !form.key || !!keyError}
        >
          {mode === "create" ? "Créer" : "Enregistrer"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TournamentFormDialog;
